/**
 * STFFLG-4185: the one list of Settings pages.
 *
 * The Settings nav, SettingsRedirect and the user menu's "Settings" entry all read this list, so
 * they cannot disagree about which pages a user has. Before it, each kept its own copy, and two of
 * them gated pages on a stand-in (Global > Roles & Permissions, or the role NAME) instead of the
 * page's own row in the role matrix.
 *
 * The pages themselves are plain data in settingsPagesData.ts (which Cypress imports); this file
 * adds what the browser needs on top: icons and the conditions beyond the row (isAvailable). A page
 * is visible when that condition passes, its route is in the user's layout, and its row allows View
 * as resolveSettingsRow decides it, the same answer the route guard gives for the page's route.
 *
 * __tests__/settingsPages.test.ts fails when a settings/* route has no entry here (or no
 * submoduleKey), or an entry has no route, so a new Settings page cannot ship outside the matrix.
 */
import {useMemo} from 'react'
import {
  ClipboardCheck,
  FileText,
  HelpCircle,
  Layout,
  List,
  PenTool,
  Settings,
  ShieldCheck,
  Video,
} from 'lucide-react'
import type {LucideIcon} from 'lucide-react'
import {ALL_ROUTES, getRoutesForVariant} from '~/src/routes/routeConfig'
import type {LayoutVariant} from '~/src/routes/routeConfig'
import {useCurrentUser} from '~/src/store/slice-hooks'
import {useAppSelector} from '~/src/store/store-hooks'
import type {BackendRolePermissions} from '~/src/utils/permissions'
import {SETTINGS_PAGES_DATA} from './settingsPagesData'
import type {SettingsPageData} from './settingsPagesData'
import {resolveSettingsRow} from './settingsPermission'

export type {SettingsRoleCategory} from './settingsPagesData'

type RoleCategory = NonNullable<BackendRolePermissions['category']>

export interface SettingsPageContext {
  category?: RoleCategory
  loggedInAgencyId?: string
  /** The signed-in user's own (primary) agency. */
  userAgencyId?: string
}

export interface SettingsPageEntry extends SettingsPageData {
  icon: LucideIcon
  /** A condition beyond the permission row, e.g. eSign's agency context. */
  isAvailable?: (ctx: SettingsPageContext) => boolean
}

/**
 * eSign Documents is managed per agency: an Agency role always works in one, an MSP role only
 * once it has switched into a child agency (its own agency is the MSP-level context), and a
 * Platform role never does.
 */
const isAgencyContext = ({category, loggedInAgencyId, userAgencyId}: SettingsPageContext) =>
  category === 'Agency' ||
  (category === 'MSP' && !!loggedInAgencyId && loggedInAgencyId !== userAgencyId)

const ICONS: Record<string, LucideIcon> = {
  'event-categories': List,
  'compliance-fields': ClipboardCheck,
  'documents-files': FileText,
  'documents-signatures': PenTool,
  'zoom-setup': Video,
  'e-verify': ShieldCheck,
  help: HelpCircle,
  'faq-admin': Settings,
  'invoice-templates': Layout,
}

const IS_AVAILABLE: Record<string, (ctx: SettingsPageContext) => boolean> = {
  'documents-files': isAgencyContext,
  'documents-signatures': isAgencyContext,
}

/** In menu order: the first visible entry is where /settings lands. */
export const SETTINGS_PAGES: readonly SettingsPageEntry[] = SETTINGS_PAGES_DATA.map((page) => ({
  ...page,
  icon: ICONS[page.id] ?? Settings,
  ...(IS_AVAILABLE[page.id] ? {isAvailable: IS_AVAILABLE[page.id]} : {}),
}))

/**
 * The layout a category gets, as AppRoutes' getVariantFromCategory picks it. With no category
 * AppRoutes falls back to the role NAME; this list never does, and skips the layout check instead
 * (the permission check below still applies).
 */
const VARIANT_BY_CATEGORY: Record<RoleCategory, LayoutVariant> = {
  Platform: 'platform',
  MSP: 'msp',
  Agency: 'agency',
  Company: 'company',
  Candidate: 'candidate',
}

const ROUTE_IDS = new Set(ALL_ROUTES.map((route) => route.id))

/**
 * The Settings pages this role may open, in menu order. Pure, so the nav, the redirect, the user
 * menu and the tests all share one answer.
 */
export const getVisibleSettingsPages = (
  permissions: BackendRolePermissions | null | undefined,
  ctx: SettingsPageContext,
): SettingsPageEntry[] => {
  // No role in the store, or a role with an empty or missing tree: every row is absent and answers
  // with its absentDefault (resolveSettingsRow), so the pages open to everyone before stay open.
  const variant = ctx.category ? VARIANT_BY_CATEGORY[ctx.category] : undefined
  const routeIdsInLayout = variant
    ? new Set(getRoutesForVariant(variant).map((route) => route.id))
    : undefined

  return SETTINGS_PAGES.filter((page) => {
    if (page.isAvailable && !page.isAvailable(ctx)) return false
    if (!ROUTE_IDS.has(page.routeId)) return false
    if (routeIdsInLayout && !routeIdsInLayout.has(page.routeId)) return false
    return resolveSettingsRow(permissions, page).canView
  })
}

/**
 * Whether the user's layout serves the notification inbox (/notifications), where /settings sends a
 * user who holds no Settings page. Company's layout has no bell and so no inbox; with no category
 * the layout check is skipped, as above.
 */
export const isInboxInLayout = (category: RoleCategory | undefined): boolean =>
  !category ||
  getRoutesForVariant(VARIANT_BY_CATEGORY[category]).some(
    (route) => route.id === 'notifications-inbox',
  )

export interface VisibleSettingsPages {
  pages: SettingsPageEntry[]
  /** The user's layout serves /notifications (see isInboxInLayout). */
  hasInbox: boolean
}

/** The current user's visible Settings pages (see getVisibleSettingsPages). */
export const useVisibleSettingsPages = (): VisibleSettingsPages => {
  const permissions = useAppSelector((state) => state.root.backendRolePermissions)
  const loggedInAgencyId = useAppSelector((state) => state.root.loggedInAgencyId)
  const {currentUser} = useCurrentUser()
  const userAgencyId = currentUser?.agencyId

  return useMemo(
    () => ({
      pages: getVisibleSettingsPages(permissions, {
        category: permissions?.category,
        loggedInAgencyId,
        userAgencyId,
      }),
      hasInbox: isInboxInLayout(permissions?.category),
    }),
    [permissions, loggedInAgencyId, userAgencyId],
  )
}
